<template>
  <div>
    <nav class="navbar navbar-top navbar-expand-md bg-white border-bottom" id="navbar-main">
      <div class="container-fluid">
        <div>
          <p class="text-uppercase text-muted mb-1 super-page-kicker">SuperAdmin</p>
          <h2 class="h3 mb-0">Operations Dashboard</h2>
        </div>
        <user-data></user-data>
      </div>
    </nav>

    <div class="container-fluid mt-0 pt-5 pt-md-8">
      <div v-if="loadError" class="alert alert-warning border-0 shadow-sm">
        {{ loadError }}
      </div>

      <div class="row">
        <div class="col-xl-6 mb-4">
          <div class="card shadow-sm border-0 h-100 super-hero">
            <div class="card-body p-4">
              <p class="super-page-kicker mb-2">Workspace</p>
              <h2 class="h2 mb-2">Run the platform from one place.</h2>
              <p class="text-muted mb-4">
                Review live platform counts, provider readiness, and recent operational activity without stitching together multiple screens.
              </p>
              <div class="d-flex flex-wrap">
                <router-link :to="{ name: 'serviceProvider' }" class="btn btn-primary mr-2 mb-2">
                  Service Providers
                </router-link>
                <router-link :to="{ name: 'siteManagement' }" class="btn btn-outline-primary mr-2 mb-2">
                  Site Management
                </router-link>
                <router-link :to="{ name: 'rateManagement' }" class="btn btn-outline-secondary mb-2">
                  Rate Management
                </router-link>
              </div>
            </div>
          </div>
        </div>

        <div class="col-xl-6 mb-4">
          <div class="card shadow-sm border-0 h-100">
            <div class="card-header bg-white border-0 py-3">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <p class="super-page-kicker mb-1">Open Sessions</p>
                  <h3 class="mb-0">Active open sessions
                    <span v-if="activeOpenSessions.length" class="badge badge-success ml-2" style="font-size: 0.8rem;">{{ activeOpenSessions.length }}</span>
                  </h3>
                </div>
              </div>
            </div>

            <div v-if="isLoading" class="text-center py-4">
              <b-spinner variant="primary" class="mb-3"></b-spinner>
              <p class="text-muted mb-0">Loading open sessions...</p>
            </div>

            <div v-else-if="activeOpenSessions.length" class="open-sessions-scroll">
              <div v-for="(session, index) in activeOpenSessions" :key="`${session.site_id}-${session.service_type_id}-${index}`" class="session-row">
                <div class="activity-marker session-marker flex-shrink-0 mt-1"></div>
                <div class="min-w-0 flex-grow-1">
                  <p class="font-weight-bold mb-0 text-truncate">{{ session.site_name || '-' }}</p>
                  <p class="text-muted mb-0 small">{{ session.service_type || '-' }} &mdash; Open since {{ formatTimestamp(session.open_since) }}</p>
                </div>
              </div>
            </div>

            <div v-else class="dashboard-empty-state">
              <h4 class="mb-2">No active open sessions</h4>
              <p class="text-muted mb-0">Active sessions will appear here.</p>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-md-6 col-xl-3 mb-4" v-for="card in totalCards" :key="card.key">
          <div class="card shadow-sm border-0 h-100 total-card">
            <div class="card-body p-4">
              <div class="d-flex justify-content-between align-items-start mb-3">
                <div>
                  <p class="super-page-kicker mb-2">{{ card.kicker }}</p>
                  <h3 class="h2 mb-0">{{ card.value }}</h3>
                </div>
                <div class="total-card__icon" :class="card.iconClass">
                  <i :class="card.icon"></i>
                </div>
              </div>
              <p class="text-muted mb-0">{{ card.label }}</p>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-xl-8 mb-4">
          <div class="card shadow-sm border-0 h-100">
            <div class="card-header bg-white border-0 py-3">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <p class="super-page-kicker mb-1">Providers</p>
                  <h3 class="mb-0">Provider breakdown</h3>
                </div>
                <router-link :to="{ name: 'serviceProvider' }" class="btn btn-sm btn-outline-primary">
                  Manage Providers
                </router-link>
              </div>
            </div>

            <div v-if="isLoading" class="text-center py-6">
              <b-spinner variant="primary" class="mb-3"></b-spinner>
              <p class="text-muted mb-0">Loading provider health...</p>
            </div>

            <div v-else-if="providerBreakdown.length" class="table-responsive">
              <table class="table align-items-center mb-0">
                <thead class="thead-light">
                  <tr>
                    <th>Provider</th>
                    <th class="text-center">Sites</th>
                    <th class="text-center">Users</th>
                    <th class="text-center">Meters</th>
                    <th class="text-center">Last Login</th>
                    <th class="text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="provider in providerBreakdown" :key="provider.id">
                    <td>
                      <div class="font-weight-bold">{{ provider.name }}</div>
                      <small class="text-muted">ID: {{ provider.id }}</small>
                    </td>
                    <td class="text-center">{{ provider.site_count != null ? provider.site_count : '-' }}</td>
                    <td class="text-center">{{ provider.user_count != null ? provider.user_count : '-' }}</td>
                    <td class="text-center">{{ provider.meter_count != null ? provider.meter_count : '-' }}</td>
                    <td class="text-center"><small>{{ provider.last_login_at ? formatDate(provider.last_login_at) : 'Never' }}</small></td>
                    <td class="text-center">
                      <span :class="provider.status ? 'badge badge-success' : 'badge badge-secondary'">
                        {{ provider.status ? 'Active' : 'Inactive' }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-else class="dashboard-empty-state">
              <h4 class="mb-2">No providers available</h4>
              <p class="text-muted mb-0">Provider breakdown will appear once the backend returns data.</p>
            </div>
          </div>
        </div>

        <div class="col-xl-4 mb-4">
          <div class="card shadow-sm border-0 h-100">
            <div class="card-header bg-white border-0 py-3">
              <div>
                <p class="super-page-kicker mb-1">Operations</p>
                <h3 class="mb-0">Recent activity</h3>
              </div>
            </div>

            <div v-if="isLoading" class="text-center py-6">
              <b-spinner variant="primary" class="mb-3"></b-spinner>
              <p class="text-muted mb-0">Loading activity feed...</p>
            </div>

            <div v-else-if="recentActivity.length" class="card-body p-4">
              <div v-for="(item, index) in recentActivity" :key="`${item.type}-${index}`" class="activity-panel-item">
                <div class="d-flex justify-content-between align-items-start mb-2">
                  <h4 class="h6 mb-0">{{ item.label || activityTypeLabel(item.type) }}</h4>
                  <small class="text-muted ml-3">{{ formatTimestamp(item.occurred_at) }}</small>
                </div>
                <p class="text-muted mb-0">{{ activityContext(item) }}</p>
              </div>
            </div>

            <div v-else class="dashboard-empty-state">
              <h4 class="mb-2">No recent activity</h4>
              <p class="text-muted mb-0">The backend has not returned any recent activity yet.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import userData from '@/pages/Common/userData'
import { formatDateTime } from '@/utils/dateFormat'

export default {
  components: {
    userData
  },
  data() {
    return {
      isLoading: false,
      loadError: ''
    }
  },
  computed: {
    ...mapGetters('superAdminDashboard', ['dashboardStats']),
    totals() {
      return this.dashboardStats && this.dashboardStats.totals ? this.dashboardStats.totals : {}
    },
    providerBreakdown() {
      return this.dashboardStats && Array.isArray(this.dashboardStats.provider_breakdown)
        ? this.dashboardStats.provider_breakdown
        : []
    },
    recentActivity() {
      return this.dashboardStats && Array.isArray(this.dashboardStats.recent_activity)
        ? this.dashboardStats.recent_activity
        : []
    },
    activeOpenSessions() {
      return this.dashboardStats && Array.isArray(this.dashboardStats.active_open_sessions)
        ? this.dashboardStats.active_open_sessions
        : []
    },
    totalCards() {
      return [
        {
          key: 'service_providers',
          kicker: 'Providers',
          label: `${this.formatCount(this.totals.active_service_providers)} active service providers`,
          value: this.formatCount(this.totals.service_providers),
          icon: 'fas fa-building',
          iconClass: 'is-blue'
        },
        {
          key: 'sites',
          kicker: 'Sites',
          label: 'Configured sites across all providers',
          value: this.formatCount(this.totals.sites),
          icon: 'fas fa-city',
          iconClass: 'is-cyan'
        },
        {
          key: 'users',
          kicker: 'Users',
          label: `${this.formatCount(this.totals.active_users)} active users`,
          value: this.formatCount(this.totals.users),
          icon: 'fas fa-users',
          iconClass: 'is-green'
        },
        {
          key: 'meters',
          kicker: 'Meters',
          label: 'Total lots currently managed',
          value: this.formatCount(this.totals.meters),
          icon: 'fas fa-tachometer-alt',
          iconClass: 'is-orange'
        }
      ]
    }
  },
  methods: {
    ...mapActions('superAdminDashboard', ['DASHBOARD_STATS']),
    formatCount(value) {
      return value != null ? value : '-'
    },
    formatDate(value) {
      return value ? formatDateTime(value) : 'Never'
    },
    formatTimestamp(value) {
      return value ? formatDateTime(value) : 'Never'
    },
    activityTypeLabel(type) {
      const labels = {
        user_login: 'User login',
        user_created: 'User created',
        site_created: 'Site created',
        service_provider_created: 'Service provider created',
        session_closed: 'Session closed'
      }

      return labels[type] || 'Platform event'
    },
    activityContext(item) {
      if (item.type === 'user_login') {
        const userName = item.user_name || 'Unknown user'
        const providerName = item.service_provider_name || 'Unknown provider'
        return `${userName} · ${providerName}`
      }

      if (item.type === 'site_created') {
        const siteName = item.site_name || 'Unknown site'
        const providerName = item.service_provider_name || 'Unknown provider'
        return `${siteName} · ${providerName}`
      }

      if (item.type === 'service_provider_created') {
        return item.service_provider_name || item.label || 'New provider'
      }

      if (item.type === 'session_closed') {
        return item.service_provider_name || item.site_name || 'Closed session'
      }

      return item.service_provider_name || item.user_name || item.site_name || 'No extra context'
    },
    loadDashboard() {
      this.isLoading = true
      this.loadError = ''

      this.DASHBOARD_STATS()
        .catch((error) => {
          this.loadError = (error && error.message) || 'Failed to load dashboard stats.'
        })
        .finally(() => {
          this.isLoading = false
        })
    }
  },
  created() {
    this.loadDashboard()
  }
}
</script>

<style scoped>
.super-page-kicker {
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  color: #5e72e4;
}

.super-hero {
  background:
    radial-gradient(circle at top right, rgba(94, 114, 228, 0.16), transparent 35%),
    linear-gradient(135deg, #ffffff 0%, #f6f8fc 100%);
}

.total-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.total-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 18px 40px rgba(50, 50, 93, 0.08);
}

.total-card__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 3.25rem;
  height: 3.25rem;
  border-radius: 1rem;
  font-size: 1.1rem;
  color: #fff;
}

.total-card__icon.is-blue {
  background: linear-gradient(135deg, #5e72e4 0%, #3d55df 100%);
}

.total-card__icon.is-cyan {
  background: linear-gradient(135deg, #11cdef 0%, #1171ef 100%);
}

.total-card__icon.is-green {
  background: linear-gradient(135deg, #2dce89 0%, #1aa56d 100%);
}

.total-card__icon.is-orange {
  background: linear-gradient(135deg, #fb6340 0%, #fbb140 100%);
}

.open-sessions-scroll {
  max-height: 280px;
  overflow-y: auto;
  padding: 0.5rem 1.25rem 1rem;
}

.session-row {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.6rem 0;
  border-bottom: 1px solid #edf2f9;
}

.session-row:last-child {
  border-bottom: none;
}

.activity-marker {
  width: 0.8rem;
  height: 0.8rem;
  margin-top: 0.35rem;
  border-radius: 50%;
  flex-shrink: 0;
  background: #5e72e4;
  box-shadow: 0 0 0 6px rgba(94, 114, 228, 0.12);
}

.session-marker {
  background: #2dce89;
  box-shadow: 0 0 0 6px rgba(45, 206, 137, 0.14);
}

.activity-panel-item + .activity-panel-item {
  margin-top: 1.25rem;
  padding-top: 1.25rem;
  border-top: 1px solid #edf2f9;
}

.dashboard-empty-state {
  padding: 2.5rem 1.5rem;
  text-align: center;
}
</style>



// WEBPACK FOOTER //
// src/pages/SuperAdmin/dashboard.vue